import { tz, TZDate } from '@date-fns/tz'
import { faClock } from '@fortawesome/free-solid-svg-icons'
import { formatDate } from '@unimpaired/utils'
import * as chrono from 'chrono-node'
import {
  format,
  getHours,
  getMinutes,
  isDate,
  isValid,
  set,
  startOfDay,
} from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import { Menu } from '../../../component-exports'
import { useIsMobile } from '../../../../hooks'
import { Input, InputProps } from '../../Input'
import { TimePicker } from './TimePicker'
import { OpenMenuButton } from './components'
import { parseDateInTimezone, userTimezone } from './helpers'

export type TimeSelectProps = Omit<
  InputProps,
  'defaultValue' | 'label' | 'name' | 'form'
> & {
  label?: string
  name?: string
  timezone?: string
  date: Date | string | undefined
  setDate: (value: Date) => void
  disableDates?: (date: Date) => boolean
}

const timeParser = chrono.casual.clone()
timeParser.parsers.push({
  pattern: () => {
    // match 1-4 digits, and a/p, returning hour/minute groups
    return /^(?:(\d{1,2})(\d{2})|(\d{1,2}))(?:([ap])m?)?$/i
  },
  extract: (context, match) => {
    const hour = parseInt(match[1] ?? match[3]) ?? 0
    const minute = parseInt(match[2] ?? 0) ?? 0
    const minuteText = String(minute).padStart(2, '0')
    const meridiem = match[4] === 'a' ? 0 : match[4] === 'p' ? 1 : undefined
    const meridiemText = meridiem !== undefined ? match[4] + 'm' : ''
    return context.createParsingResult(
      0,
      `${hour}:${minuteText} ${meridiemText}`,
      {
        // chrono ignores meridiem here; fold the 12h clock
        hour: meridiem === undefined ? hour : (hour % 12) + 12 * meridiem,
        minute,
      },
    )
  },
})

export const TimeSelect = ({
  name = 'time',
  date: naturalDate,
  setDate: setParentDate,
  timezone,
  ...props
}: TimeSelectProps) => {
  const options = { in: tz(timezone ?? userTimezone) }
  const newDate = new TZDate(new Date(), timezone ?? userTimezone)
  const date = !naturalDate
    ? newDate
    : isDate(naturalDate)
    ? new TZDate(naturalDate, timezone ?? userTimezone)
    : parseDateInTimezone(
        timeParser,
        naturalDate,
        newDate,
        timezone ?? userTimezone,
      ) ?? newDate

  const parseNaturalLanguageDate = (datestring: string) =>
    new TZDate(
      parseDateInTimezone(
        timeParser,
        datestring,
        startOfDay(date, options),
        timezone ?? userTimezone,
        { forwardDate: true },
      ) ?? date,
      timezone ?? userTimezone,
    )

  const setDate = (newDate: Date | '') => {
    if (!newDate || !isDate(newDate)) return setParentDate(date)

    setParentDate(
      set(date, {
        hours: getHours(newDate, options),
        minutes: getMinutes(newDate, options),
      }),
    )
  }

  if (!isDate(naturalDate)) setDate(date)

  const setNaturalLanguageDate = (newDate: string) => {
    const parsedDate = parseNaturalLanguageDate(newDate)
    // disallowed input: snap the field back to the valid date
    if (props.disableDates?.(parsedDate)) {
      mockForm.setValue(name, date, {
        shouldValidate: true,
      })
      return
    }
    setDate(parsedDate)
    mockForm.setValue(name, parsedDate, {
      shouldValidate: true,
    })
  }

  const [menuOpen, setMenuOpen] = useState(false)
  const isMobile = useIsMobile()
  const ref = useRef<HTMLButtonElement>(null)
  const mockForm = useForm({
    defaultValues: {
      [name]: date,
    },
    mode: 'onChange',
  })

  // update the forms date when the outside date prop changes
  useEffect(() => {
    mockForm.setValue(
      name,
      !naturalDate
        ? date
        : isDate(naturalDate)
        ? naturalDate
        : parseNaturalLanguageDate(naturalDate),
    )
  }, [naturalDate])

  return (
    <>
      <Menu
        className='flex items-stretch gap-4 min-w-72'
        triggerRef={ref}
        onClose={() => setMenuOpen(false)}
        isOpen={menuOpen}>
        <TimePicker
          className='grow'
          date={date}
          disableDates={props.disableDates}
          setDate={(date) => {
            setDate(date)
            setMenuOpen(false)
          }}
          timezone={timezone ?? userTimezone}
        />
      </Menu>
      {/* @ts-expect-error */}
      <Input
        {...props}
        form={mockForm}
        label={props.label ?? 'Time'}
        name={name}
        // On mobile we set type to time to use the native pickers
        type={isMobile ? 'time' : 'text'}
        format={(e) =>
          !isDate(e)
            ? e
            : !isMobile
            ? // on desktop we can use a nice long readable format
              formatDate('time', e, timezone ?? userTimezone)
            : // on mobile set the format of the time to match the format of the html time input
              format(e, 'HH:mm', { in: tz(timezone ?? userTimezone) })
        }
        // text input has no native picker button; add ours
        appendElement={
          !isMobile && (
            <OpenMenuButton
              ref={ref}
              icon={faClock}
              onClick={() => setMenuOpen((open) => !open)}
            />
          )
        }
        inputClasses={twMerge('bg-base-0', isMobile && 'pr-1')}
        validate={{
          'Enter a valid time': (e) =>
            isValid(new Date(e)) || isValid(timeParser.parseDate(e)),
        }}
        // desktop parses on blur only: partial input mid-typing
        onChange={isMobile ? setNaturalLanguageDate : undefined}
        onBlur={setNaturalLanguageDate}
      />
    </>
  )
}
